import { ampli, Environment } from '../ampli';
import { 
  amplitude, 
  amplitudeContentTypes, 
  amplitudeSearchSchoolTypes, 
  amplitudeSearchSortedByTypes,
  AdditionalContentProperties,
  DataType,
  AmpliContentPayload,
  ampliSubscriptionType,
  AmpliSubscriptionSubmitted,
  // AmpliSubscriptionSubmittedPayload,
  amplitudePaymentMethodTypes,
  amplitudeBilingCicleTypes,
  mapNodeEnvToAmpliEnv,
  AmpliUserSubscriptionProperties,
  ampliMigrationSource,
  searchAlgorithm,
  ContentViewedPageSource,
} from '../constants';
import {
  AmplitudeTrackPayload, ContentType, EventType
} from '@klab-berlin/api-sdk/lib/types/requests/Track';
import { SearchFilterAction, SearchPayload } from '../types/tracking';
import { User } from '../types/User';
import { SubscriptionResponse } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { isFeatureEnabled } from './feature';
import { isRenewedSubscription } from './subscription';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { Product } from '@klab-berlin/api-sdk/lib/types/responses/Product';
import { ConversationMessageContext } from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import { useEffect, useState } from 'react';
import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';
import { CollectionProperties } from '../types/AppContent';
import { getDateInGermanFormat } from './format';
import searchResultsStorageManager from './searchResultsStorageManager';
import { v4 as uuidv4 } from 'uuid';

const ExperimentKey: Record<Environment, string> = {
  production: 'client-lRP88RCfsPHYyhULhyZlMBoofdX7wj0S',
  development: 'client-lfl0groDXkD6rICz0pPglMfIhjmSBuAi'
};

const ExperimentUrl = 'https://api.lab.eu.amplitude.com';

interface ExperimentVariants {
  [key: string]: {
    [key: string]: string;
  };
}

interface Experiment {
  client?: ExperimentClient;
  variants: ExperimentVariants;
  key?: string;
  loaded: boolean;
}

const experiment: Experiment = { loaded: false, variants: {} };
const pendingFetches: Record<string, Promise<string>> = {};

// Amplitude readiness management
const AMPLI_READY_TIMEOUT_MS = 10000;

interface AmpliReadinessManager {
  isReady: boolean;
  readyPromise: Promise<void> | null;
  resolveReady: (() => void) | null;
  timeoutId: ReturnType<typeof setTimeout> | null;
  waitForReady: () => Promise<void>;
  setReady: () => void;
}

const ampliReadiness: AmpliReadinessManager = {
  isReady: false,
  readyPromise: null,
  resolveReady: null,
  timeoutId: null,
  waitForReady() {
    if (ampliReadiness.isReady) {
      return Promise.resolve();
    }
    if (!ampliReadiness.readyPromise) {
      ampliReadiness.readyPromise = new Promise((resolve) => {
        ampliReadiness.resolveReady = resolve;
        ampliReadiness.timeoutId = setTimeout(() => {
          ampliReadiness.timeoutId = null;
          ampliReadiness.setReady();
        }, AMPLI_READY_TIMEOUT_MS);
      });
    }
    return ampliReadiness.readyPromise;
  },
  setReady() {
    if (ampliReadiness.timeoutId) {
      clearTimeout(ampliReadiness.timeoutId);
      ampliReadiness.timeoutId = null;
    }
    ampliReadiness.isReady = true;
    if (ampliReadiness.resolveReady) {
      ampliReadiness.resolveReady();
    }
    ampliReadiness.resolveReady = null;
  }
};

/**
 * Wait for Amplitude SDK to be fully initialized.
 * Resolves immediately if already ready, or waits until initialization completes.
 */
export const waitForAmpliReady = (): Promise<void> => {
  return ampliReadiness.waitForReady();
};

/**
 * Mark Amplitude SDK as ready (called after ampli.load() completes).
 * @internal
 */
export const setAmpliReady = (): void => {
  ampliReadiness.setReady();
};

/** @internal Reset readiness state — for tests only */
export const resetAmpliReadiness = (): void => {
  if (ampliReadiness.timeoutId) {
    clearTimeout(ampliReadiness.timeoutId);
    ampliReadiness.timeoutId = null;
  }
  ampliReadiness.isReady = false;
  ampliReadiness.readyPromise = null;
  ampliReadiness.resolveReady = null;
};

/**
 * Initializes the experimentClient if it's not done already
 * @returns experimentClient
 */
const getExperimentClient = () => {
  if (!experiment.key) {
    if (process.env.NODE_ENV && process.env.NODE_ENV in mapNodeEnvToAmpliEnv) {
      experiment.key = ExperimentKey[mapNodeEnvToAmpliEnv[process.env.NODE_ENV]];
    }
  }
  if (experiment.key && !experiment.client) {
    experiment.client = Experiment.initializeWithAmplitudeAnalytics(experiment.key, {
      automaticExposureTracking: true,
      automaticFetchOnAmplitudeIdentityChange: true,
      serverUrl: ExperimentUrl
    });
  }
  experiment.loaded = !!experiment.key && !!experiment.client;
  return experiment.client;
};

/**
 * Fetch variation for experiment
 *
 * @param user
 * @param experimentKey
 * @returns variation
 */
export const fetchExperimentVariation = async (user: User, experimentKey: string): Promise<string> => {
  if (!isFeatureEnabled('ampli')) return 'control';
  experiment.variants[user.id] = experiment.variants[user.id] || {};
  const userVariants = experiment.variants[user.id];
  if (userVariants && userVariants[experimentKey]) {
    return userVariants[experimentKey];
  }
  const cacheKey = `${user.id}:${experimentKey}`;
  if (cacheKey in pendingFetches) {
    return pendingFetches[cacheKey];
  }
  pendingFetches[cacheKey] = (async () => {
    await waitForAmpliReady();
    const experimentClient = experiment.loaded ? experiment.client : getExperimentClient();
    if (!!ampli.client && experimentClient) {
      const newUser = mapAmpliUserProperties(user);
      ampli.identify(user.id, newUser);
      const result = await experimentClient.fetch();
      const variant = result.variant(experimentKey).value || 'off';
      userVariants[experimentKey] = variant;
      return variant;
    }
    return 'control';
  })().finally(() => {
    delete pendingFetches[cacheKey];
  });
  return pendingFetches[cacheKey];
};

export const useAmplitudeExperiment = (user?:User, experimentKey?:string) => {
  const [isReady, setIsReady] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [variant, setVariant] = useState('control');
  useEffect(() => {
    if (user && user.id && experimentKey) {
      setIsReady(false);
      setIsEnabled(false);
      fetchExperimentVariation(user, experimentKey)
        .then(variant => {
          setIsEnabled(variant !== 'off');
          setVariant(variant === 'off' ? 'control' : variant);
        })
        .catch(() => {
          setIsEnabled(false);
          setVariant('control');
        })
        .finally(() => {
          setIsReady(true);
        });
    }
  }, [user?.id, experimentKey]);
  return { isReady, isEnabled, variant };
};

export const convertArrayToString = (array: string[]|number[]): string => {
  return array.join(', ');
};

export const mapTeacherType = (isTrainee: boolean | undefined): string =>{
  if (isTrainee === true){
    return amplitude.teacherType.ref;
  } else if (isTrainee === false){
    return amplitude.teacherType.regular;
  } else {
    return amplitude.teacherType.keineAngabe;
  }
};

export const mapAmpliPayloadProperties = (
  listOfProperties: Record<string, any>,
  source: Record<string, any>
): Record<string, any> => { 
  const properties: Record<string, any> = {};

  for (const property in listOfProperties) {
    const value = listOfProperties[property];

    if (value !== undefined && value !== null) {
      const keyMapped = source[property];
      if (keyMapped) properties[keyMapped] = value;
    }
  }

  return properties;
};

export const mapAmpliUserProperties = (userProperties: Record<string, any>): Record<string, any> => {
  const properties = { ...userProperties };

  if (properties.type) properties.type = amplitude.mapUserType[properties.type];

  if (properties.signUpMethod) properties.signUpMethod = amplitude.mapSignupMethod[properties.signUpMethod];

  if (properties.subjects){
    const majors = properties.subjects.majors.map((major: any) => major.subject);
    const minors = properties.subjects.minors.map((minor: any) => minor.subject);
    properties.subjects = convertArrayToString([...majors, ...minors]);

    if (!properties.isSubjectSelected) properties.firstSubject = majors[0] || null;
  }

  if (properties.classes) properties.classYear = convertArrayToString(properties.classes);

  if ('isTrainee' in properties && properties.isTrainee !== undefined) {
    properties.teacherType = mapTeacherType(properties.isTrainee);
  }

  if (properties?.lbMigration?.date) {  // Only set migration properties if the user was migrated from LB
    properties.migrationSource = ampliMigrationSource.LEHRERBUERO;
    properties.migrationCohort = properties.lbMigration?.cohortDescription;
    properties.migratedDate = getDateInGermanFormat(properties.lbMigration.date);
  }

  return mapAmpliPayloadProperties(properties, amplitude.userAmpliMap);
};

export const mapAmpliSearchProperties = (searchProperties: Record<string, any>): Record<string, any> => {
  const amplitudeSearchProperties: Record<string, any> = {};
  const { searchAmpliMap } = amplitude;
  const { 
    schoolType: schoolTypes, 
    minClassYear,
    maxClassYear,
    subject,
    subTopic,
    publisher,
    classYear,
    learningFormat,
    teachingFormat,
    teachingMethod,
    pageContentType,
    inclusionAdapted,
    courseTheme,
    courseDuration,
    courseType
  } = searchProperties;

  if (schoolTypes) {   
    amplitudeSearchProperties[searchAmpliMap.schoolType] = schoolTypes
      .split(',')
      .map((schoolType: string) => amplitudeSearchSchoolTypes[schoolType]);
  }

  if (minClassYear && maxClassYear) {
    amplitudeSearchProperties[searchAmpliMap.classYears] =
      getAmpliClassYearsArrayFromMinAndMaxYearValues(+minClassYear, +maxClassYear);
  }

  if (classYear) {
    const rawClassYears = Array.isArray(classYear) ? classYear : String(classYear).split(',');
    amplitudeSearchProperties[searchAmpliMap.classYears] = rawClassYears
      .map(Number)
      .filter((n) => !isNaN(n));
  }

  if (subject) amplitudeSearchProperties[searchAmpliMap.subject] = subject;

  if (inclusionAdapted !== undefined) {
    amplitudeSearchProperties[searchAmpliMap.inclusionAdapted] = String(inclusionAdapted);
  }

  const arrayProperties = {
    subTopic,
    publisher,
    learningFormat,
    teachingFormat,
    teachingMethod,
    pageContentType,
    courseTheme,
    courseDuration,
    courseType
  };

  for (const property in arrayProperties) { 
    const propertyValue: string = searchProperties[property];
    if (propertyValue) {
      const keyMapped: string = amplitude.searchAmpliMap[property];
      amplitudeSearchProperties[keyMapped] = propertyValue.split(',');
    }
  }
  
  return amplitudeSearchProperties;
};

export const getAmpliSessionId = (): number|undefined => {
  return isFeatureEnabled('ampli') ? ampli.client.getSessionId() : undefined;
}; 

export const getAmpliDeviceId = (): string|undefined => {
  return isFeatureEnabled('ampli') ? ampli.client.getDeviceId() : undefined;
};

interface NewSearchJourneyOptions {
  searchSuggestionApplied?: boolean;
  relatedTopicsApplied?: boolean;
}

/**
 * Starts a new search journey by generating a unique search ID. Persists the
 * `searchSuggestionApplied` / `relatedTopicsApplied` flags so the downstream
 * SearchCompleted event (fired in a different component after results load)
 * can read them. Flags from the prior journey are cleared when not provided.
 */
export const startNewSearchJourney = (options: NewSearchJourneyOptions = {}): string => {
  const searchId = uuidv4();
  searchResultsStorageManager.setSearchId(searchId);
  searchResultsStorageManager.setSearchSuggestionApplied(options.searchSuggestionApplied);
  searchResultsStorageManager.setRelatedTopicsApplied(options.relatedTopicsApplied);
  return searchId;
};

/**
 * Gets the current search ID from session storage
 * @returns The current search ID, or undefined if no search has been performed
 */
export const getCurrentSearchId = (): string | undefined => {
  return searchResultsStorageManager.getSearchId();
};

/**
 * Gets the search ID from session storage or falls back to the search_id URL query param.
 */
export const getSearchIdWithFallback = (): string | undefined => {
  return getCurrentSearchId()
    || new URLSearchParams(window.location.search).get('search_id')
    || undefined;
};

/**
 * Clears the current search ID from session storage.
 * Should be called when accessing content from non-search sources
 * (favorites, bookmarks, dashboard, downloads, direct access).
 */
export const clearSearchId = (): void => {
  const searchState = searchResultsStorageManager.getSearchState();
  if (searchState.searchId) {
    delete searchState.searchId;
    searchResultsStorageManager.setSearchState(searchState);
  }
};

export const getContentEventPayload = (
  data: {
    content?: any,
    contentType: ContentType
    currentPage?: number,
    searchId?: string,
    pageSource?: ContentViewedPageSource,
    event_type: EventType,
  }
): AmplitudeTrackPayload | null => {
  const sessionId: number | undefined = getAmpliSessionId();
  const deviceId: string | undefined = getAmpliDeviceId();
  const {
    event_type,
    currentPage,
    content,
    contentType,
    searchId,
    pageSource,
  } = data;

  if (sessionId && content) {
    let payload: AmplitudeTrackPayload = {
      event_properties: {
        content: [
          mapAmpliPayloadProperties({ ...content, contentType }, amplitude.contentAmpliMap)
        ],
        'page number': currentPage,
        ...(searchId && { 'search id': searchId }),
        ...(pageSource && { 'page source': pageSource }),
      },
      event_type: event_type,
      'session_id': sessionId,
    };

    if (deviceId) {
      payload = {
        ...payload,
        'device_id': deviceId
      };
    }

    return payload;
  }

  return null;
};

export interface SearchEventData {
  term: SearchPayload['search term'],
  query: Record<string, any>,
  previousQuery?: Record<string, any>,
  appSection?: SearchPayload['app section'],
  tab?: string,
  results?: number,
  searchAlgorithm?: string,
  searchSuggestionApplied?: boolean,
  relatedTopicsApplied?: boolean,
  searchId?: string,
}

export const getAmpliSearchEventPayload = (data: SearchEventData) => {
  const { query } = data;
  const searchFilter: Record<string, any>[] = [ mapAmpliSearchProperties(query) ];
  const payload: SearchPayload = {
    'search term': data.term,
    'search sorted by': query['sortBy'] ? 
      amplitudeSearchSortedByTypes[query['sortBy']] : 
      amplitudeSearchSortedByTypes.relevance,
    'search filter': searchFilter,
    'app section': data.appSection === 'courses' ? 'courses' : 'library',
    'search algorithm': data.searchAlgorithm || searchAlgorithm.BM25_BASELINE,
    ...(data.searchSuggestionApplied != null && { 'search suggestion applied': data.searchSuggestionApplied }),
    ...(data.relatedTopicsApplied != null && { 'related topics applied': data.relatedTopicsApplied }),
    ...(data.searchId && { 'search id': data.searchId }),
  };

  if (searchFilter?.length) {
    const currentSearchFilter = searchFilter[0];
    const appliedFilters: string[] = getAmpliSearchFilterAppliedProperty(currentSearchFilter);

    if (data.previousQuery) {
      const previousSearchFilter = mapAmpliSearchProperties(data.previousQuery);
      const filtersChanged = JSON.stringify(previousSearchFilter) !== JSON.stringify(currentSearchFilter);
      if (filtersChanged) {
        const previousFilters = getAmpliSearchFilterAppliedProperty(previousSearchFilter);
        const filterAction = getSearchFilterAction(previousFilters.length, appliedFilters.length);
        if (filterAction) {
          payload['filter action'] = filterAction;
        }
      }
    }

    if (appliedFilters.length && payload['search filter']) {
      payload['search filter'][0]['search filters applied'] = appliedFilters;
    }
  }

  return payload;
};

export const getSearchFilterAction = (previousCount: number, newCount: number): SearchFilterAction | undefined => {
  if (previousCount === 0 && newCount === 0) return undefined;
  if (previousCount === 0 && newCount > 0) return 'apply';
  if (previousCount > 0 && newCount === 0) return 'clear_all';
  return 'amend';
};

export const getLicenceIdFromSubscription = (subscription?: SubscriptionResponse): number | undefined => {
  return subscription?.licence?.licenceModelId ?
    +subscription?.licence?.licenceModelId :
    undefined;
};

const getAmpliClassYearsArrayFromMinAndMaxYearValues = (minClassYear: number, maxClassYear: number): number[] => {   
  const numberArray: number[] = [];
  for (let i = minClassYear; i <= maxClassYear; i++) {
    numberArray.push(i);
  }
  
  return numberArray;
};

const getAmpliSearchFilterAppliedProperty = (data: any): string[] => {
  const avaliableSearchFilters: string[] = [
    'search filter subject', 
    'search filter topics', 
    'search filter school types', 
    'search filter publishers', 
    'search filter class years', 
    'search filter educational format',
    'search filter exercise format',
    'search filter material scopes',
    'search filter differentiation',
    'search filter course types',
    'search filter course themes',
    'search filter course durations'
  ];

  const appliedFilters: string[] = [];
  avaliableSearchFilters.forEach((filter) => data[filter] && appliedFilters.push(filter));

  return appliedFilters;
};

export const getAdditionalAmpliContentProperties = {
  mindItem: (data: Partial<MindItem> | Partial<Product>): AdditionalContentProperties => {
    let content;
    if ('fileType' in data && data.fileType === 'video') {
      content = {
        contentType: amplitudeContentTypes.video,
        publicVideo: 'No'
      };
    } else if ('fileType' in data && data.fileType === 'course') {
      content = {
        contentType: amplitudeContentTypes.course,
        courseTheme: data?.course?.theme,
        courseDuration: data?.course?.duration,
        courseType: data?.course?.type,
      };
    } else {
      content = { 
        contentType: data.isReadOnly ? amplitudeContentTypes.readOnlyDocument : amplitudeContentTypes.document,
      };
    }
    return content;
  },
  product: () => ({
    contentType: amplitudeContentTypes.product,
  }),
  blankPage: () => ({
    contentType: amplitudeContentTypes.blankPage,
  }),
  richText : () => ({
    contentType: amplitudeContentTypes.richText,
  }),
};

export const getAmpliContentData = (data: Partial<MindItem> | Partial<Product>, dataType: DataType): any => {
  const additionalContentProperties = getAdditionalAmpliContentProperties[dataType](data);
  return [mapAmpliPayloadProperties({ ...data, ...additionalContentProperties }, amplitude.contentAmpliMap)];
};

// Maps the API-facing podcast values to the Amplitude taxonomy values.
const ampliPodcastFormatMap: Record<string, string> = {
  explainer: 'monolog',
  dialog: 'dialog',
};

const ampliPodcastDurationMap: Record<number, string> = {
  1: 'short',
  3: 'medium',
  5: 'long',
};

export const getAmpliPodcastTemplateParameters = (
  data?: ConversationMessageContext['data'],
): Record<string, string>[] => {
  if (!data) return [];

  const templateParameters = mapAmpliPayloadProperties({
    format: typeof data.format === 'string' ? ampliPodcastFormatMap[data.format] : undefined,
    duration: typeof data.duration === 'number' ? ampliPodcastDurationMap[data.duration] : undefined,
    language: typeof data.language === 'string' ? data.language : undefined,
  }, amplitude.podcastTemplateParametersAmpliMap);

  return Object.keys(templateParameters).length > 0 ? [templateParameters] : [];
};

export const getAmpliContentEventPayload =
  (data: Partial<MindItem> | Partial<Product>, dataType: DataType, props: any = {}): AmpliContentPayload => {
    const additionalEventProperties = { ...getAdditionalAmpliContentProperties[dataType](data), ...props };
 
    return {
      content: [
        mapAmpliPayloadProperties({ 
          ...data,
          ...additionalEventProperties 
        }, amplitude.contentAmpliMap)
      ],
    };
  };

export const getAmpliSubscriptionSubmittedPayload = ({ 
  licence,
  billingCycle,
  promoCode,
  paymentMethod,
  userType,
  licenceModelId,
}: AmpliSubscriptionSubmitted): AmplitudeTrackPayload  => {
  const previousUserType: number | undefined = userType ? userType : undefined;
  const previousLicenceId: number | undefined = licenceModelId ? Number(licenceModelId) : undefined;

  const sessionId: number | undefined = getAmpliSessionId();
  const deviceId: string | undefined = getAmpliDeviceId();
  const payload : AmplitudeTrackPayload =  {
    event_properties: {
      ...mapAmpliPayloadProperties({ 
        licenseId: licence.licenceModelId,
        subscriptionType: licence.isRef ? ampliSubscriptionType.REF : ampliSubscriptionType.TEACHER,
        subscriptionInterval: `${licence.period}mo`,
        billingCycle: amplitudeBilingCicleTypes[billingCycle],
        promoCode,
        paymentMethod: amplitudePaymentMethodTypes[paymentMethod],
        previousUserType,
        previousLicenceId
      }, amplitude.premiumSubscriptionSubmittedMap) 
    },
    event_type: 'Subscription Submitted',
    'session_id': sessionId,
  } as AmplitudeTrackPayload;
  if (deviceId) {
    payload.device_id = deviceId;
  }
  return payload;
};

// Modifying date format to YY/MM/DD
const getCorrectDateFormatForSubscriptionProperties = (date?: string): string | undefined => {
  if (!date?.length) return undefined;

  const newDate: Date = new Date(date);
  return newDate.toLocaleDateString('en-GB', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  }).split('/').reverse().join('/');
};

export const getAmpliSubscriptionUserProperties = (
  subscription?: SubscriptionResponse
): AmpliUserSubscriptionProperties => {
  if (!subscription) return {};
  
  let subscriptionRenewed: string | undefined;
  let subscriptionStatus: string | undefined;
  // The trial subscription cannot be renewed.
  if (subscription?.trial?.isInTrial || subscription?.licence?.isSchoolTrial) {
    subscriptionRenewed = undefined;
    subscriptionStatus = 'trial';
  } else {
    subscriptionStatus = subscription?.status?.toLocaleLowerCase();
    subscriptionRenewed = isRenewedSubscription(subscription) ? subscription.startDate : undefined;
  }

  const getSubscriptionTypeForSubscriptionProperties: Record<string, 'ref' | 'teacher' | 'school' | 'library'>  = {
    trainee: 'ref',
    teacher: 'teacher',
    school: 'school',
    free: 'teacher',
    library: 'library',
    student: 'library' 
  };

  const subscriptionType: string = subscription.licence?.isSchoolTrial ? 
    getSubscriptionTypeForSubscriptionProperties.school : 
    getSubscriptionTypeForSubscriptionProperties[subscription.licence.category.toLowerCase()];

  return {
    'subscription type': subscriptionType,
    'subscription status': subscriptionStatus,
    'subscription interval': `${subscription.licence.term}mo`,
    'subscription end': getCorrectDateFormatForSubscriptionProperties(subscription.endDate),
    'subscription start': getCorrectDateFormatForSubscriptionProperties(subscription.originalCreatedDate),
    'promo code': subscription.promoCode || undefined,
    'subscription mmr': subscription.price,
    'license id': getLicenceIdFromSubscription(subscription),
    'school id': subscription?.schoolId || undefined,
    'subscription renewed': getCorrectDateFormatForSubscriptionProperties(subscriptionRenewed),
  };
};

export const getAmpliCommonCollectionProperties = (collection: CollectionProperties) => {
  return {
    'collection id': collection.id,
    'collection name': collection.title,
    'collection description': collection.description,
    'collection school type': collection.schoolType,
    'collection school stage': collection.schoolStage,
    'collection subjects': collection.subjects && collection.subjects.join(', ')
  };
};
